import { useCallback, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/AuthContext';

export function LoginPage() {
  const navigate = useNavigate();
  const { login, isLoading, isAuthenticated, loginNotice, clearLoginNotice } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const onUsernameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      clearLoginNotice();
      setUsername(event.target.value);
    },
    [clearLoginNotice]
  );

  const onPasswordChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      clearLoginNotice();
      setPassword(event.target.value);
    },
    [clearLoginNotice]
  );

  const onSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setError(null);
      clearLoginNotice();
      const result = await login(username, password);
      if (result.success) {
        navigate('/', { replace: true });
        return;
      }
      setError(result.error?.message ?? 'Login failed');
    },
    [clearLoginNotice, login, navigate, password, username]
  );

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <div
      data-testid="demo-login"
      className="flex flex-1 items-center justify-center px-4 py-10"
    >
      <form
        onSubmit={(e) => void onSubmit(e)}
        className="w-full max-w-md space-y-6 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm dark:border-slate-700 dark:bg-slate-900"
        data-testid="demo-login-form"
      >
        <div className="space-y-2">
          <p className="font-body text-xs font-semibold uppercase tracking-[0.16em] text-indigo-600">
            Kincode AI
          </p>
          <h1 className="type-h1">Demo console</h1>
          <p className="type-body">
            Usa tu login de Kincode AI para enviarte insights, pulsos o recordatorios KAI.
          </p>
        </div>

        <label className="block space-y-1.5">
          <span className="type-label">Email</span>
          <input
            data-testid="demo-login-username"
            type="email"
            autoComplete="username"
            value={username}
            onChange={onUsernameChange}
            className="demo-input"
            required
          />
        </label>

        <label className="block space-y-1.5">
          <span className="type-label">Contraseña</span>
          <input
            data-testid="demo-login-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={onPasswordChange}
            className="demo-input"
            required
          />
        </label>

        {loginNotice ? (
          <p
            data-testid="demo-login-notice"
            className="font-body text-sm text-amber-800 dark:text-amber-300"
            role="status"
          >
            {loginNotice}
          </p>
        ) : null}

        {error ? (
          <p
            data-testid="demo-login-error"
            className="font-body text-sm text-red-700 dark:text-red-400"
            role="alert"
          >
            {error}
          </p>
        ) : null}

        <button
          data-testid="demo-login-submit"
          type="submit"
          disabled={isLoading}
          className="demo-btn-primary"
        >
          {isLoading ? 'Entrando…' : 'Entrar'}
        </button>
      </form>
    </div>
  );
}
